/**
 * Field-type-aware filter operators.
 *
 * Two operator vocabularies live here, bridged in one place:
 * - `FilterOperator` — the structured-filter ("chip") vocabulary (eq/neq/in…)
 *   used by URL filters and `filter-to-clause`.
 * - `RQB_OPERATORS` — the react-querybuilder operator list rendered in the
 *   visual builder dropdown (search + custom policies).
 *
 * `operatorsForFieldType` is the single gating helper both query-builder
 * wrappers use to narrow the dropdown per field type. Adding a query-builder
 * operator is a change in THIS file (label here, emit in
 * `processors/rule-processor`, parse in `processors/parse-opensearch`).
 */

import { canonicalizeField } from '../utils/prefixer'

import type { Operator } from 'react-querybuilder'

export type FieldType =
	| 'string'
	| 'number'
	| 'date'
	| 'boolean'
	| 'ip'
	| 'array'

export type FilterOperator =
	| 'eq'
	| 'neq'
	| 'in'
	| 'not_in'
	| 'gt'
	| 'lt'
	| 'gte'
	| 'lte'
	| 'contains'
	| 'exists'

export const OPERATORS_BY_FIELD_TYPE: Record<
	FieldType,
	ReadonlyArray<FilterOperator>
> = {
	string: ['eq', 'neq', 'in', 'not_in', 'contains', 'exists'],
	number: ['eq', 'neq', 'in', 'not_in', 'gt', 'lt', 'gte', 'lte', 'exists'],
	date: ['eq', 'neq', 'gt', 'lt', 'gte', 'lte', 'exists'],
	boolean: ['eq', 'neq', 'exists'],
	ip: ['eq', 'neq', 'in', 'not_in', 'exists'],
	array: ['contains', 'exists'],
}

export const NEGATING_OPERATORS = new Set<FilterOperator>(['neq', 'not_in'])

/**
 * The react-querybuilder operator vocabulary for the visual builder dropdown.
 * Shared verbatim by Search and the custom-policy editor. `like`/`notlike`
 * map to `match` (analyzed, no `.keyword`); see `processors/rule-processor`.
 */
export const RQB_OPERATORS: Array<Operator> = [
	{ name: '=', label: '=' },
	{ name: 'like', label: 'like' },
	{ name: 'notlike', label: 'not like' },
	{ name: '!=', label: '!=' },
	{ name: '<', label: '<' },
	{ name: '>', label: '>' },
	{ name: '<=', label: '<=' },
	{ name: '>=', label: '>=' },
	{ name: 'contains', label: 'contains' },
	{ name: 'beginsWith', label: 'begins with' },
	{ name: 'endsWith', label: 'ends with' },
	{ name: 'doesNotContain', label: 'does not contain' },
	{ name: 'doesNotBeginWith', label: 'does not begin with' },
	{ name: 'doesNotEndWith', label: 'does not end with' },
	{ name: 'matchPhrase', label: 'matches phrase' },
	{ name: 'notMatchPhrase', label: 'does not match phrase' },
	{ name: 'regexp', label: 'matches regex' },
	{ name: 'notRegexp', label: 'does not match regex' },
	{ name: 'notNull', label: 'exists' },
	{ name: 'null', label: 'does not exist' },
	{ name: 'in', label: 'in' },
	{ name: 'notIn', label: 'not in' },
	{ name: 'between', label: 'between' },
	{ name: 'notBetween', label: 'not between' },
]

/**
 * Map a `FilterOperator` (field-type vocabulary) to the react-querybuilder
 * operator name(s) it enables in the dropdown.
 */
const FILTER_OP_TO_RQB: Record<FilterOperator, Array<string>> = {
	eq: ['='],
	neq: ['!='],
	in: ['in'],
	not_in: ['notIn'],
	gt: ['>'],
	lt: ['<'],
	gte: ['>='],
	lte: ['<='],
	contains: ['contains'],
	exists: ['notNull', 'null'],
}

/**
 * RQB operators with no `FilterOperator` equivalent (free text / regex / phrase).
 * Offered only on text-like field types — they don't apply to numbers/dates.
 */
const FREE_TEXT_RQB_OPS = [
	'like',
	'notlike',
	'matchPhrase',
	'notMatchPhrase',
	'regexp',
	'notRegexp',
]
const FREE_TEXT_FIELD_TYPES = new Set<string>(['string', 'array'])

/**
 * Build a `getOperators(fieldName)` for react-querybuilder that narrows the
 * full operator list to the subset a field's type accepts. Shared by the
 * Search and custom-policy query builders (was duplicated in both).
 *
 * @param fieldTypeLookup - Map of canonical field id → `FieldType`.
 * @param allOperators - The operator list to filter (defaults to `RQB_OPERATORS`).
 * @returns A `(fieldName: string) => Array<Operator>` selector.
 */
export function operatorsForFieldType(
	fieldTypeLookup: Map<string, string>,
	allOperators: Array<Operator> = RQB_OPERATORS,
) {
	return (fieldName: string): Array<Operator> => {
		const canonical = canonicalizeField(fieldName)
		const fieldType = fieldTypeLookup.get(canonical) ?? 'string'
		const allowed =
			OPERATORS_BY_FIELD_TYPE[fieldType as FieldType] ??
			OPERATORS_BY_FIELD_TYPE.string
		const rqbAllowed = new Set<string>(
			allowed.flatMap((op) => FILTER_OP_TO_RQB[op] ?? []),
		)
		if (FREE_TEXT_FIELD_TYPES.has(fieldType)) {
			for (const op of FREE_TEXT_RQB_OPS) rqbAllowed.add(op)
		}
		return allOperators.filter((op) => rqbAllowed.has(op.name))
	}
}
